import { Controller } from "@hotwired/stimulus"
import { getViewsRelativePath, getViewId } from "../helpers/view_helpers";

export default class extends Controller {
  static targets = ["resizableHeader"]

  connect() {

    this.startPageX = 0;
    this.startColumnWidth = 0; // Stores the column width at the start of a drag operation
    this.currentHeader = null;
    this.activeHandle = null; // Keep track of the currently "selected" handle
    this.autoSizeCharacterCalculatorSpan = $("#autoSizeCharacterCalculatorSpan") || null
    this.paddingLeft = parseInt(this.autoSizeCharacterCalculatorSpan?.css("padding-left").replace(/px$/, ''), 10) || 12;
    this.paddingRight = parseInt(this.autoSizeCharacterCalculatorSpan?.css("padding-right").replace(/px$/, ''), 10) || 12;
    this.paddingTotal = this.paddingLeft + this.paddingRight;
    this.screenWidth = window.innerWidth;
    this.userRole = window.Flexmls.currentUser.role?.toLowerCase() || "public";

    // Check for mobile browser (excluding iPad)
    this.isMobile = (
      /Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && !/iPad/.test(navigator.userAgent) ||
      this.screenWidth < 768
    );
    this.manualResizeEnabled = this.manualResizeEnabled();

    if (this.autoSizeCharacterCalculatorSpan?.width()) {
      this.paddingTotal += this.autoSizeCharacterCalculatorSpan.width();
    } else {
      this.paddingTotal += 8;
    }

    if (this.manualResizeEnabled) {
      this.addResizeHandles();
    }

  }

  manualResizeEnabled() {
    return this.userRole === "private" && this.isMobile === false;
  }

  resizeSkeletonLoadingDivs() {
    const skeletonDivsContainer = $("#tableListingsListLoadingSpinner");
    const listingsDataTable = $("#listingsDataTable");
    const tableWidth = listingsDataTable?.width();

    if (skeletonDivsContainer && tableWidth) {
      skeletonDivsContainer.width(tableWidth);
    }
  }

  setColumnWidths() {
    // This runs:
    // on page load
    // when columns are resized
    // when the view is changed
    // after Moarify adds listings to the table

    // Get view_id
    const viewId = getViewId();

    const headers = $(".listingsTableStickyHeader > tr > th").map(function(){ return $(this).attr("id")}).get();
    const customColumnWidths = window.customColumnWidths?.[viewId];

    headers.forEach(id => {
      let name = id.replace(/^th-/, '');
      let column = $(`#${id}`);

      if (!this.isMobile && customColumnWidths?.[name]) {
        column.css("width", customColumnWidths[name]);
      } else if (window.autoSizer?.[viewId]?.[name]) {
          const characterCount = window.autoSizer[viewId][name];
          let headerAutoSize = "";

          if (name == "price" || name == "mls-num-col") {
            headerAutoSize = this.autoSizeCharacterCalculatorSpan?.text("8".repeat(characterCount));
          } else {
            headerAutoSize = this.autoSizeCharacterCalculatorSpan?.text("A".repeat(characterCount));
          }
          headerAutoSize = headerAutoSize.width();
          const padding = this.paddingTotal;

          headerAutoSize = this.maxWidthCheck(headerAutoSize + padding);

          column.css("width", headerAutoSize);
          this.autoSizeCharacterCalculatorSpan.text("A");
      } else {
        let headerAutoSize = this.autoSizeCharacterCalculatorSpan?.text($(column).text().trim())?.width();
        const padding = this.paddingTotal;
        headerAutoSize = this.maxWidthCheck(headerAutoSize + padding);

        column.css("width", headerAutoSize);
        this.autoSizeCharacterCalculatorSpan.text("A");
      }

    })

    this.resizeSkeletonLoadingDivs();
  }

  maxWidthCheck(width) {
    if (this.isMobile) {
      if (width > this.screenWidth) {
        return this.screenWidth - this.paddingLeft;
      }
      return Math.min(width, 500);
    }
    return Math.min(width, 500);
  }

  refresh() {

    if (this.manualResizeEnabled) {
      this.removeResizeHandles();
      this.addResizeHandles();
    }

    this.setColumnWidths();
  }

  removeResizeHandles() {
    this.resizableHeaderTargets.forEach(header => {
      // Remove click listener from header
      header.removeEventListener('click', this.handleHeaderClick);

      const existingHandle = header.querySelector('.resize-handle');
      if (existingHandle) {
        existingHandle.removeEventListener('mousedown', this.startResize.bind(this));
        if (this.manualResizeEnabled) {
          existingHandle.removeEventListener('touchstart', this.startResize.bind(this));
        }
        header.removeChild(existingHandle);
      }
    });

    this.removeListeners();

    // Also remove the click-outside-to-deselect listener
    document.removeEventListener('click', this.deselectHandleOnClickOutside);

    this.element.classList.remove('resizing');
    this.deselectHandle(); // Ensure any active handle is deselected
  }

  removeListeners() {
    document.removeEventListener('mousemove', this.resizeColumn);
    document.removeEventListener('mouseup', this.stopResize);

    if (this.manualResizeEnabled) {
      document.removeEventListener('touchmove', this.resizeColumn);
      document.removeEventListener('touchend', this.stopResize);
    }
  }

  addListeners() {
    document.addEventListener('mousemove', this.resizeColumn);
    document.addEventListener('mouseup', this.stopResize);
    if (this.manualResizeEnabled) {
      document.addEventListener('touchmove', this.resizeColumn, { passive: false });
      document.addEventListener('touchend', this.stopResize);
    }
  }

  addResizeHandles() {
    if (this.resizableHeaderTargets.length === 0) {
      return;
    }

    this.resizableHeaderTargets.forEach((header, index) => {
      // Attach click listener to the header itself
      header.addEventListener('click', this.handleHeaderClick.bind(this));
      header.dataset.columnIndex = index; // Ensure header has index for easy lookup

      if (!header.querySelector('.resize-handle')) {
        const handle = document.createElement('div');
        handle.classList.add('resize-handle');
        handle.dataset.columnIndex = index; // Handle also gets index
        header.appendChild(handle);
        handle.addEventListener('mousedown', this.startResize.bind(this));
        if (this.manualResizeEnabled) {
          handle.addEventListener('touchstart', this.startResize.bind(this), { passive: false });
        }
      }
    });

    // Add a global click listener to deselect the handle when clicking outside the table or header
    document.addEventListener('click', this.deselectHandleOnClickOutside.bind(this));
  }

  handleHeaderClick(event) {
    // Prevent the click from propagating up and immediately deselecting if clicked inside table
    event.stopPropagation();

    const clickedHeader = event.currentTarget;
    const clickedHandle = clickedHeader.querySelector('.resize-handle');

    if (clickedHandle) {
      if (this.activeHandle === clickedHandle) {
        // If the same handle is clicked again, deselect it
        this.deselectHandle();
      } else {
        // Otherwise, select this handle
        this.selectHandle(clickedHandle);
      }
    }
  }

  selectHandle(handle) {
    // Deselect any previously active handle
    if (this.activeHandle && this.activeHandle !== handle) {
      this.activeHandle.classList.remove('active');
      this.activeHandle.parentNode.classList.remove('active-handle-parent'); // Remove from parent header
    }

    // Select the new handle
    handle.classList.add('active');
    handle.parentNode.classList.add('active-handle-parent'); // Add class to parent header
    this.activeHandle = handle;
  }

  deselectHandle() {
    if (this.activeHandle) {
      this.activeHandle.classList.remove('active', 'is-resizing');
      this.activeHandle.parentNode.classList.remove('active-handle-parent'); // Remove from parent header
      this.activeHandle = null;
    }
  }

  deselectHandleOnClickOutside(event) {
    // If the click is outside the controller element AND there's an active handle, deselect it
    if (!this.element.contains(event.target) && this.activeHandle) {
      this.deselectHandle();
    }
  }

  startResize(event) {
    // Check if the handle being dragged is the active handle
    // if (this.activeHandle !== event.target) {
    //   // If it's not the active handle, prevent resize, but still allow selection
    //   this.selectHandle(event.target); // Select the handle that was clicked
    //   return; // Stop the resize process
    // }

    event.preventDefault();
    event.stopPropagation();

    this.element.classList.add('resizing');
    event.target.classList.add('is-resizing');

    // Deselect the handle when dragging starts (optional, but makes sense for active drag)
    // You might want to remove this if you want the 'active' state to persist during drag.
    // However, typically 'resizing' state replaces 'active' for visual feedback during drag.
    this.deselectHandle();

    let pageX;
    if (event.type === 'touchstart' && !this.isMobile) {
      pageX = event.touches[0].pageX;
    } else {
      pageX = event.pageX;
    }

    const columnIndex = parseInt(event.target.dataset.columnIndex, 10);
    this.currentHeader = this.resizableHeaderTargets[columnIndex];

    // Capture the current rendered width of the header at the start of the resize
    this.startColumnWidth = this.currentHeader.offsetWidth;
    this.startPageX = pageX;

    this.addListeners();

  }

  resizeColumn = (event) => {
    if (!this.currentHeader) return;

    event.preventDefault();

    let currentPageX;
    if (event.type === 'touchmove' && !this.isMobile) {
      currentPageX = event.touches[0].pageX;
    } else {
      currentPageX = event.pageX;
    }

    const diffX = currentPageX - this.startPageX;
    // Calculate new width based on the column's width when the drag started
    let newWidth = this.startColumnWidth + diffX;

    const columnIndex = parseInt(this.currentHeader.dataset.columnIndex, 10);
    // Use the minimum content width if available, otherwise default to 50px
    const minContentWidth = (this.columnMinWidths ? this.columnMinWidths.get(columnIndex) : null) || 50;

    newWidth = Math.max(newWidth, minContentWidth);

    this.currentHeader.style.width = `${newWidth}px`;
  }

  stopResize = () => {
    this.element.classList.remove('resizing');
    // this.element.classList.remove('is-resizing');
    this.startColumnWidth = 0; // Reset this value for the next resize operation

    // Remove is-resizing class from the handle that was being dragged
    if (this.currentHeader) {
      const handle = this.currentHeader.querySelector('.resize-handle');
      if (handle) {
        handle.classList.remove('is-resizing');
      }
    }

    this.deselectHandle();
    this.removeListeners();

    const viewId = getViewId();
    const url = getViewsRelativePath() + viewId;
    const columnName = this.currentHeader.id.replace(/^th-/, '');
    const columnWidth = this.currentHeader.style.width;
    const currentCustomColumnWidth = window.customColumnWidths?.[viewId]?.[columnName];

    // Don't run the ajax call if the width is unchanged
    if (currentCustomColumnWidth && currentCustomColumnWidth === columnWidth) {
      return;
    }

    let customColumnWidthData = {};

    if (viewId && columnName && columnWidth) {
      customColumnWidthData = {
        "customColumnWidths": {
          [viewId]: {
            [columnName]: columnWidth
          }
        }
      }

      $.ajax({
        url: url,
        dataType: 'json',
        type: 'PUT',
        data: customColumnWidthData,
        success: function(d){
          // Nothing to do here
        },
        error: function(e){
          console.log('error' + JSON.stringify(e))
        }
      })

      window.customColumnWidths[viewId] = {...window.customColumnWidths[viewId], ...{[columnName]:columnWidth}};
    }

    this.currentHeader = null;

  }

  disconnect() {
    this.removeResizeHandles();
  }

}
